import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from '@/components/layout/Sidebar';
import { TopBar } from '@/components/layout/TopBar';
import AuthGuard from '@/components/auth/AuthGuard';
import AdminGuard from '@/components/auth/AdminGuard';
import { ModuleShell } from '@/components/dashboard/ModuleShell';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { ToastContainer } from '@/components/ui/ToastContainer';
import { Toaster } from 'sonner';
import { FinancialProvider } from '@/lib/contexts/FinancialContext';
import { I18nProvider } from '@/lib/contexts/I18nContext';
import { ThemeProvider } from '@/lib/contexts/ThemeContext';
import ScrollToTop from '@/components/common/ScrollToTop';
import { AICopilot } from '@/components/copilot/AICopilot';

// Lazy load actual pages
const DashboardOverview = lazy(() => import('./pages/dashboard/DashboardOverview'));
const CompaniesPage = lazy(() => import('./pages/dashboard/CompaniesPage'));
const GestionPage = lazy(() => import('./pages/dashboard/companies/GestionPage'));
const LegalDzAiPage = lazy(() => import('./pages/dashboard/companies/LegalDzAiPage'));
const SimulatorPage = lazy(() => import('./pages/dashboard/companies/SimulatorPage'));
const SimulatorResultPage = lazy(() => import('./pages/dashboard/companies/SimulatorResultPage'));

const FinancePage = lazy(() => import('./pages/dashboard/FinancePage'));
const GenvestPage = lazy(() => import('./pages/dashboard/GenvestPage'));
const SettingsPage = lazy(() => import('./pages/dashboard/SettingsPage'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const SignupPage = lazy(() => import('./pages/auth/SignupPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const StatutesGeneratorPage = lazy(() => import('./pages/dashboard/companies/StatutesGeneratorPage'));
const CreationPage = lazy(() => import('./pages/dashboard/companies/CreationPage'));
const CreationResultPage = lazy(() => import('./pages/dashboard/companies/CreationResultPage'));
const NameCheckPage = lazy(() => import('./pages/dashboard/companies/NameCheckPage'));
const VerificationPage = lazy(() => import('./pages/dashboard/companies/VerificationPage'));

// New dedicated legal module pages
const PartnersPage = lazy(() => import('./pages/dashboard/companies/PartnersPage'));
const IntegrationsPage = lazy(() => import('./pages/dashboard/companies/IntegrationsPage'));
const ConformitePage = lazy(() => import('./pages/dashboard/companies/ConformitePage'));
const NameCheckResultPage = lazy(() => import('./pages/dashboard/companies/NameCheckResultPage'));
const StatutesGeneratorResultPage = lazy(() => import('./pages/dashboard/companies/StatutesGeneratorResultPage'));
const DocumentsVaultPage = lazy(() => import('./pages/dashboard/companies/DocumentsVaultPage'));

// Public subpages
const PublicLayout = lazy(() => import('./components/layout/PublicLayout'));
const LegalPage = lazy(() => import('./pages/public/LegalPage'));
const PrivacyPage = lazy(() => import('./pages/public/PrivacyPage'));
const SecurityPublicPage = lazy(() => import('./pages/public/SecurityPage'));
const TermsPage = lazy(() => import('./pages/public/TermsPage'));
const ContactPage = lazy(() => import('./pages/public/ContactPage'));
const SupportPage = lazy(() => import('./pages/public/SupportPage'));
const AuditPage = lazy(() => import('./pages/public/AuditPage'));
const HQPage = lazy(() => import('./pages/public/HQPage'));
const PricingPage = lazy(() => import('./pages/public/PricingPage'));

// 💰 FINANCE MODULE PAGES
const AlertesPage = lazy(() => import('./pages/dashboard/finance/AlertesPage'));
const GestionFinancierePage = lazy(() => import('./pages/dashboard/finance/GestionFinancierePage'));
const AdvisorInsightPage = lazy(() => import('./pages/dashboard/finance/AdvisorInsightPage'));
const TresoreriePage = lazy(() => import('./pages/dashboard/finance/TresoreriePage'));
const EvaluationProjetPage = lazy(() => import('./pages/dashboard/finance/EvaluationProjetPage'));
const ProjetsSIPage = lazy(() => import('./pages/dashboard/finance/ProjetsSIPage'));
const ETaxesPage = lazy(() => import('./pages/dashboard/finance/ETaxesPage'));
const RapportsFinanciersPage = lazy(() => import('./pages/dashboard/finance/RapportsFinanciersPage'));
const RapportResultPage = lazy(() => import('./pages/dashboard/finance/RapportResultPage'));
const KpisDatasPage = lazy(() => import('./pages/dashboard/finance/KpisDatasPage'));
const KpisDatasResultPage = lazy(() => import('./pages/dashboard/finance/KpisDatasResultPage'));
const FinancePartnersPage = lazy(() => import('./pages/dashboard/finance/PartnersPage'));
const RisquesPage = lazy(() => import('./pages/dashboard/genvest/RisquesPage'));
const SimulationRisquesInputPage = lazy(() => import('./pages/dashboard/genvest/SimulationRisquesInputPage'));
const SimulationRisquesResultPage = lazy(() => import('./pages/dashboard/genvest/SimulationRisquesResultPage'));
const PortfolioSimulationInputPage = lazy(() => import('./pages/dashboard/genvest/PortfolioSimulationInputPage'));
const PortfolioSimulationResultPage = lazy(() => import('./pages/dashboard/genvest/PortfolioSimulationResultPage'));
const PortfolioCreationInputPage = lazy(() => import('./pages/dashboard/genvest/PortfolioCreationInputPage'));
const PortfolioCreationResultPage = lazy(() => import('./pages/dashboard/genvest/PortfolioCreationResultPage'));

const PortfolioPage = lazy(() => import('./pages/dashboard/genvest/PortfolioPage'));
const AIPredictivePage = lazy(() => import('./pages/dashboard/genvest/AIPredictivePage'));
const OpportunitiesPage = lazy(() => import('./pages/dashboard/genvest/OpportunitiesPage'));
const InstitutionalFundingPage = lazy(() => import('./pages/dashboard/genvest/InstitutionalFundingPage'));
const GenvestIntegrationsPage = lazy(() => import('./pages/dashboard/genvest/IntegrationsPage'));
const GenvestAlertesPage = lazy(() => import('./pages/dashboard/genvest/AlertesPage'));


const DatabasesPage = lazy(() => import('./pages/dashboard/infrastructure/DatabasesPage'));
const SecurityPage = lazy(() => import('./pages/dashboard/infrastructure/SecurityPage'));
const ConnectivityPage = lazy(() => import('./pages/dashboard/infrastructure/ConnectivityPage'));
const RecoveryPage = lazy(() => import('./pages/dashboard/infrastructure/RecoveryPage'));
const CRMPage = lazy(() => import('./pages/dashboard/infrastructure/CRMPage'));
const InfrastructureOverviewPage = lazy(() => import('./pages/dashboard/infrastructure/InfrastructureOverviewPage'));
const AdminDashboard = lazy(() => import('./pages/dashboard/admin/AdminDashboard'));

const DashboardLayout = () => {
  const { pathname } = useLocation();
  const isRootDashboard = pathname === '/dashboard' || pathname === '/dashboard/';

  return (
    <AuthGuard>
      <div className="flex h-screen bg-bg-primary text-text-primary overflow-hidden font-sans selection:bg-accent-primary/30">
        <Sidebar />
        <div className="flex-1 flex flex-col relative overflow-hidden">
          <TopBar />
          <main className="flex-1 overflow-hidden relative">
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </main>
        </div>
      </div>
    </AuthGuard>
  );
};


export default function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <FinancialProvider>
          <Router>
            <ScrollToTop />
            <AICopilot />
            <Suspense fallback={
              <div className="flex h-screen w-full items-center justify-center bg-bg-primary text-text-muted">
                <div className="flex flex-col items-center gap-4">
                  <div className="w-12 h-12 border-4 border-accent-primary/20 border-t-accent-primary rounded-full animate-spin"></div>
                  <p className="text-sm font-medium tracking-widest uppercase animate-pulse">Initialisation du système...</p>
                </div>
              </div>
            }>
                <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/auth" element={<Navigate to="/auth/signup" replace />} />
                <Route path="/auth/login" element={<LoginPage />} />
                <Route path="/auth/signup" element={<SignupPage />} />
                <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/auth/reset-password" element={<ResetPasswordPage />} />

                <Route element={<PublicLayout />}>
                  <Route path="/legal" element={<LegalPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/security" element={<SecurityPublicPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/support" element={<SupportPage />} />
                  <Route path="/audit" element={<AuditPage />} />
                  <Route path="/hq" element={<HQPage />} />
                  <Route path="/pricing" element={<PricingPage />} />
                </Route>

                <Route path="/dashboard" element={<DashboardLayout />}>
                  <Route index element={<DashboardOverview />} />

                  {/* 🏛️ LEGAL SYSTEM - FULLY OPTIMIZED 100vh DASHBOARDS */}
                  <Route path="companies">
                    <Route index element={<CompaniesPage />} />
                    <Route path="validation-marque">
                      <Route index element={<NameCheckPage />} />
                      <Route path="result" element={<NameCheckResultPage />} />
                    </Route>
                    <Route path="ai-legal" element={<LegalDzAiPage />} />
                    <Route path="simulator">
                      <Route index element={<SimulatorPage />} />
                      <Route path="result" element={<SimulatorResultPage />} />
                    </Route>

                    <Route path="creer-entreprise">
                      <Route index element={<CreationPage />} />
                      <Route path="result" element={<CreationResultPage />} />
                    </Route>
                    <Route path="gestion" element={<GestionPage />} />
                    <Route path="documents">
                      <Route index element={<DocumentsVaultPage />} />
                      <Route path="generate" element={<StatutesGeneratorPage />} />
                      <Route path="result" element={<StatutesGeneratorResultPage />} />
                    </Route>
                    <Route path="partners" element={<PartnersPage />} />
                    <Route path="integrations" element={<IntegrationsPage />} />
                    <Route path="conformite" element={<ConformitePage />} />
                  </Route>

                  {/* 💰 FINANCE SYSTEM */}
                  <Route path="finance">
                    <Route index element={<FinancePage />} />
                    <Route path="alertes" element={<AlertesPage />} />
                    <Route path="comptabilite" element={<GestionFinancierePage />} />
                    <Route path="advisor" element={<AdvisorInsightPage />} />
                    <Route path="tresorerie" element={<TresoreriePage />} />
                    <Route path="evaluation" element={<EvaluationProjetPage />} />
                    <Route path="projets" element={<ProjetsSIPage />} />
                    <Route path="e-taxes" element={<ETaxesPage />} />
                    <Route path="reports" element={<RapportsFinanciersPage />} />
                    <Route path="reports/result" element={<RapportResultPage />} />
                    <Route path="indicateurs">
                      <Route index element={<KpisDatasPage />} />
                      <Route path="result" element={<KpisDatasResultPage />} />
                    </Route>
                    <Route path="partenaires" element={<FinancePartnersPage />} />
                  </Route>

                  {/* 📈 INVESTMENT SYSTEM (GENVEST) */}
                  <Route path="genvest">
                    <Route index element={<GenvestPage />} />
                    <Route path="portfolio-simulation">
                      <Route index element={<PortfolioSimulationInputPage />} />
                      <Route path="result" element={<PortfolioSimulationResultPage />} />
                    </Route>

                    <Route path="creation">
                      <Route index element={<PortfolioCreationInputPage />} />
                      <Route path="result" element={<PortfolioCreationResultPage />} />
                    </Route>
                    
                    <Route path="portefeuille" element={<PortfolioPage />} />
                    <Route path="risques">
                      <Route index element={<RisquesPage />} />
                      <Route path="simulation" element={<RisquesPage />} />
                    </Route>
                    <Route path="simulation-risques">
                      <Route index element={<SimulationRisquesInputPage />} />
                      <Route path="result" element={<SimulationRisquesResultPage />} />
                    </Route>
                    <Route path="ai-predictive" element={<AIPredictivePage />} />
                    <Route path="opportunites" element={<OpportunitiesPage />} />
                    <Route path="deal-flow" element={<InstitutionalFundingPage />} />
                    <Route path="integrations" element={<GenvestIntegrationsPage />} />
                    <Route path="alertes" element={<GenvestAlertesPage />} />
                  </Route>
                  {/* 👑 ADMIN SYSTEM */}
                  <Route path="admin" element={
                    <AdminGuard>
                      <AdminDashboard />
                    </AdminGuard>
                  } />

                  {/* ⚡ INFRASTRUCTURE SYSTEM */}
                  <Route path="infrastructure" element={
                    <AdminGuard>
                      <Outlet />
                    </AdminGuard>
                  }>
                    <Route index element={<InfrastructureOverviewPage />} />
                    <Route path="databases" element={<DatabasesPage />} />
                    <Route path="security" element={<SecurityPage />} />
                    <Route path="crm" element={<CRMPage />} />
                    <Route path="connectivity" element={<ConnectivityPage />} />
                    <Route path="recovery" element={<RecoveryPage />} />
                  </Route>

                  <Route path="settings" element={<SettingsPage />} />
                </Route>

                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Suspense>
          </Router>
          <ToastContainer />
          <Toaster position="top-right" expand={true} richColors={true} />
        </FinancialProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}
